import { ProgressBar } from '@tremor/react'
import { ChevronDown, CircleAlert, DatabaseIcon, File, LucideIcon, MessageCircle, Sparkles, Table } from 'lucide-react'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useClickAway } from 'react-use'
import { motion, AnimatePresence } from 'framer-motion'
import { useUsage } from '~/hooks/usage/useWorkspaceUsages'
import { QUOTA_TYPE_USER_FACING_PROPERTIES_MAP } from '~/features/usage/constants'
import { Button, Separator, useSidebar } from '@bpinternal/ui-kit-next'
import { Link } from '@tanstack/react-router'
import { formatValueWithUnit } from '~/features/usage/helpers'
import { QuotaType } from '@bpinternal/const'

type NavUsageProps = {
  workspaceId: string
}

type UsageItem = {
  current: number
  max: number
  label: string
  icon: LucideIcon
  type: QuotaType
  percentage: number
  description: string
}

const QUOTA_ICONS: Partial<Record<QuotaType, LucideIcon>> = {
  ai_spend: Sparkles,
  storage_count: File,
  invocation_calls: MessageCircle,
  table_row_count: Table,
  knowledgebase_vector_storage: DatabaseIcon,
}

const USAGE_ORDER: QuotaType[] = [
  'ai_spend',
  'storage_count',
  'invocation_calls',
  'table_row_count',
  'knowledgebase_vector_storage',
]

export function NavUsages({ workspaceId }: NavUsageProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const { state: sidebarState } = useSidebar()

  const currentPeriod = new Date().toISOString().split('T')[0] ?? ''
  const usagesRecord = useUsage({
    workspaceId,
    quotas: USAGE_ORDER,
    period: currentPeriod,
  })

  const usages: UsageItem[] = useMemo(() => {
    return USAGE_ORDER.map((type) => {
      const usage = usagesRecord[type]
      if (!usage) return null

      const Icon = QUOTA_ICONS[type] || MessageCircle
      const quota = QUOTA_TYPE_USER_FACING_PROPERTIES_MAP[type]
      const label = quota?.shortName || quota?.name || type

      const descriptor = type === 'invocation_calls' ? ' calls' : type === 'table_row_count' ? ' rows' : ''
      const description = `${formatValueWithUnit(usage.value, type)} of ${formatValueWithUnit(usage.quota || 1, type)}${descriptor} used`

      return {
        current: usage.value,
        max: usage.quota || 1,
        label,
        icon: Icon,
        type,
        percentage: (usage.value / (usage.quota || 1)) * 100,
        description,
      }
    }).filter((usage) => usage !== null) as UsageItem[]
  }, [usagesRecord])

  const [mainUsage, setMainUsage] = useState<UsageItem | undefined>(undefined)
  const [additionalUsages, setAdditionalUsages] = useState<UsageItem[]>([])
  const [hasHighUsage, setHasHighUsage] = useState(false)

  useEffect(() => {
    if (usages.length === 0) {
      setMainUsage(undefined)
      setAdditionalUsages([])
      setHasHighUsage(false)
      return
    }

    const highUsage = usages.some((usage) => usage.percentage > 60)
    setHasHighUsage(highUsage)

    let newMainUsage: UsageItem | undefined
    let newAdditionalUsages: UsageItem[] = []

    const aiSpendUsage = usages.find((usage) => usage.type === 'ai_spend')
    // If there's no high usage, show AI spend as the main one. Otherwise show the highest usage
    if (aiSpendUsage && !highUsage) {
      newMainUsage = aiSpendUsage
      newAdditionalUsages = usages.filter((usage) => usage.type !== 'ai_spend')
    } else {
      const sortedUsages = [...usages].sort((a, b) => b.percentage - a.percentage)
      newMainUsage = sortedUsages[0]
      newAdditionalUsages = usages.filter((usage) => usage.type !== newMainUsage?.type)
    }

    setMainUsage(newMainUsage)
    setAdditionalUsages(newAdditionalUsages)
  }, [usages])

  const toggleExpanded = () => setIsExpanded(!isExpanded)

  useClickAway(buttonRef, () => setIsExpanded(false))

  if (!mainUsage || sidebarState === 'collapsed') {
    return null
  }

  return (
    <button
      ref={buttonRef}
      onClick={toggleExpanded}
      className="relative m-1 flex flex-col gap-4 rounded-lg border border-gray-3 bg-background p-3 transition-all hover:border-gray-6 hover:shadow-sm active:opacity-80"
    >
      <motion.div
        className="absolute right-2 top-2 h-6 w-6"
        animate={{ rotate: isExpanded ? 0 : 180 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      >
        <ChevronDown />
      </motion.div>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="flex flex-col gap-4 overflow-hidden"
          >
            <p className="text-start text-xs text-muted-foreground">Usage</p>
            {additionalUsages.map((usage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -10 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.15, delay: index * 0.05, ease: 'easeInOut' },
                }}
                exit={{
                  opacity: 0,
                  transition: { duration: 0.1, ease: 'easeInOut' },
                }}
              >
                <AdditionalUsage usage={usage} />
              </motion.div>
            ))}
            <Separator />
          </motion.div>
        )}
      </AnimatePresence>
      {mainUsage && <MainUsage usage={mainUsage} />}
      {hasHighUsage && (
        <Button asChild variant="secondary">
          <Link to="/workspaces/$workspaceId/usage" params={{ workspaceId }} onClick={(e) => e.stopPropagation()}>
            Manage
          </Link>
        </Button>
      )}
    </button>
  )
}

function MainUsage({ usage }: { usage: Required<UsageItem> }) {
  return (
    <div className="flex flex-col gap-3 truncate">
      <div className="flex flex-row items-center gap-1">
        <usage.icon size={16} />
        <p className="text-sm font-medium">{usage.label}</p>
      </div>
      <ProgressBar
        value={usage.percentage}
        color={usage.percentage >= 80 ? 'red' : usage.percentage >= 60 ? 'yellow' : 'blue'}
      />
      {usage.percentage < 100 && <p className="text-end text-sm text-muted-foreground">{usage.description}</p>}
      {usage.percentage >= 100 && (
        <div className="flex flex-row items-center justify-end gap-1">
          <p className="text-sm text-danger">Usage limit reached</p>
          <CircleAlert size={16} className="text-danger" />
        </div>
      )}
    </div>
  )
}

function AdditionalUsage({ usage }: { usage: UsageItem }) {
  const compactDescription = usage.description.replace(' of ', '/').replace(' used', '')

  return (
    <div className="group/usage flex flex-col gap-3 truncate">
      <div className="flex flex-row items-center justify-between">
        <div className="flex min-w-0 flex-1 flex-row items-center gap-1">
          <usage.icon size={16} className="flex-shrink-0" />
          <p className="truncate text-sm font-medium">{usage.label}</p>
        </div>
        <div className="ml-2 flex-shrink-0 text-sm text-muted-foreground">
          <p className="group-hover/usage:hidden">{usage.percentage.toFixed(0)}%</p>
          <p className="hidden group-hover/usage:block">{compactDescription}</p>
        </div>
      </div>
      <ProgressBar
        value={usage.percentage}
        color={usage.percentage >= 80 ? 'red' : usage.percentage >= 60 ? 'yellow' : 'blue'}
      />
    </div>
  )
}
